/**
 * Mapeo de IDs de campos a códigos de traducción para las explicaciones/tooltips
 * Los textos reales estarán en los archivos de traducciones (es.json, en.json, etc.)
 * 
 * Formato: fieldId -> código de traducción
 * Ejemplo: 'CPA_Conversiones_primarias' -> 'field_descriptions.cpa_primary_conversions'
 * 
 * Nota: Los IDs pueden venir en diferentes formatos (ej: 'CPA_Conversiones_primarias', 'FIELD_CPA_PRIMARY_CONVERSIONS')
 * Se deben agregar todas las variantes posibles aquí.
 *
 * ⚠️ Cada clave de traducción tiene que existir en `es.json` Y en `en.json`: desde `274eb33` el «?»
 * aparece en todo campo de este mapa, y sin su texto el cartel sale vacío o en otro idioma. Lo vigila
 * `utils/__tests__/fieldDescriptions.bundles.test.js`. (Card x4TLNeOw: se sacaron diez entradas de
 * ids que el catálogo no tiene —`clicks`, `ctr`, `day`…; el catálogo dice `Clicks`, `CTR`, `Fecha`—
 * y cuyas claves no existían en ningún bundle.)
 */
export const FIELD_DESCRIPTIONS = {
    // Métricas de Conversiones y CPA - Variantes posibles
    'cpa_primary_conversions': 'field_descriptions.cpa_primary_conversions',
    'CPA_PRIMARY_CONVERSIONS': 'field_descriptions.cpa_primary_conversions',
    'FIELD_CPA_PRIMARY_CONVERSIONS': 'field_descriptions.cpa_primary_conversions',
    'CPA_Conversiones_primarias': 'field_descriptions.cpa_primary_conversions',
    
    'primary_conversions': 'field_descriptions.primary_conversions',
    'PRIMARY_CONVERSIONS': 'field_descriptions.primary_conversions',
    'FIELD_PRIMARY_CONVERSIONS': 'field_descriptions.primary_conversions',
    'Conversiones_Primarias': 'field_descriptions.primary_conversions',
    
    'selected_events_conversions': 'field_descriptions.selected_events_conversions',
    'SELECTED_EVENTS_CONVERSIONS': 'field_descriptions.selected_events_conversions',
    'FIELD_SELECTED_EVENTS_CONVERSIONS': 'field_descriptions.selected_events_conversions',
    'Conversiones_de_Eventos_seleccionados': 'field_descriptions.selected_events_conversions',
    'Eventos_Seleccionados': 'field_descriptions.selected_events_conversions',

    'GA4__Eventos_seleccionados': 'field_descriptions.ga4_selected_events',

    'cpa_selected_events': 'field_descriptions.cpa_selected_events',
    'CPA_SELECTED_EVENTS': 'field_descriptions.cpa_selected_events',
    'FIELD_CPA_SELECTED_EVENTS': 'field_descriptions.cpa_selected_events',
    'CPA_Eventos_seleccionados': 'field_descriptions.cpa_selected_events',
    
    // Presupuestos - Variantes posibles
    'business_unit_budget': 'field_descriptions.business_unit_budget',
    'BUSINESS_UNIT_BUDGET': 'field_descriptions.business_unit_budget',
    'FIELD_BUSINESS_UNIT_BUDGET': 'field_descriptions.business_unit_budget',
    'Presupuesto_de_línea_de_control': 'field_descriptions.business_unit_budget',
    'Presupuesto_de_linea_de_control': 'field_descriptions.business_unit_budget',
    
    'campaign_budget': 'field_descriptions.campaign_budget',
    'CAMPAIGN_BUDGET': 'field_descriptions.campaign_budget',
    'FIELD_CAMPAIGN_BUDGET': 'field_descriptions.campaign_budget',
    'Presupuesto_de_Campaña_de_línea_de_control': 'field_descriptions.campaign_budget',
    'Presupuesto_de_Campana_de_linea_de_control': 'field_descriptions.campaign_budget',
    
    'adgroup_budget': 'field_descriptions.adgroup_budget',
    'ADGROUP_BUDGET': 'field_descriptions.adgroup_budget',
    'FIELD_ADGROUP_BUDGET': 'field_descriptions.adgroup_budget',
    'Presupuesto_de_Grupo_de_anuncios_de_línea_de_control': 'field_descriptions.adgroup_budget',
    'Presupuesto_de_Grupo_de_anuncios_de_linea_de_control': 'field_descriptions.adgroup_budget',

    // LinkedIn Ads - País / Región del público: datos aproximados (card ctbIdOOc)
    'Linkedin_Pais': 'field_descriptions.linkedin_geo',
    'Linkedin_Region': 'field_descriptions.linkedin_geo',

    // Plataforma vs. GA4 (tanda «Feedback de Hesta», E8/E13). Cada texto se verificó contra la
    // fórmula del Data Node (`settings.js`) y del catálogo (`looker_cc/fields_data.py`): ROAS es
    // SUM(Valor_de_compra)/SUM(Costo), ROAS (GA4) es Ingresos de GA4 / Costo, la tasa es
    // SUM(Eventos_Seleccionados)/SUM(Clicks)*100. Si cambia una fórmula, cambia el texto.
    'Valor_de_compra': 'field_descriptions.purchase_value',
    'GA4__Ingresos_por_compras': 'field_descriptions.ga4_purchase_revenue',
    'ROAS': 'field_descriptions.roas',
    // ⚠️ Hoy NADIE la lee: `GA4__ROAS` está en `FIELD_DATA_BY_ID` del catálogo pero en ninguna
    // fuente de `FIELDS_BY_DATA_SOURCE_TYPE`, y el Data Node no tiene su fórmula (01/10). El texto
    // sale de la del Tablero (`GA4_ROAS_METRIC_NAME`: ingresos de GA4 / costo). El día que Informes
    // la ofrezca, verificarlo contra la fórmula del nodo antes de dar este texto por bueno.
    'GA4__ROAS': 'field_descriptions.ga4_roas',
    'Conv_Rate_Eventos_seleccionados': 'field_descriptions.conv_rate_selected_events',

    // Las dimensiones de GA4 (grupo `GA4` del catálogo): el nodo las declara sólo para la fuente
    // GA4 (`data_sources: [DATA_SOURCE_GA4]`), así que en una fila de pauta van vacías.
    'GA4__Fuente': 'field_descriptions.ga4_only_dimension',
    'GA4__Medio': 'field_descriptions.ga4_only_dimension',
    'GA4__Fuente_medio': 'field_descriptions.ga4_only_dimension',
    'GA4__Campaña_sesión_dim': 'field_descriptions.ga4_only_dimension',
    'GA4__Agrupación_canal_sesión': 'field_descriptions.ga4_only_dimension',
    'GA4__Fuente_primer_usuario': 'field_descriptions.ga4_only_dimension',
    'GA4__Medio_primer_usuario': 'field_descriptions.ga4_only_dimension',
    'GA4__Fuente_medio_primer_usuario': 'field_descriptions.ga4_only_dimension',
    'GA4__Agrupación_canal_primer_usuario': 'field_descriptions.ga4_only_dimension',
    'GA4__País': 'field_descriptions.ga4_only_dimension',
    'GA4__Región': 'field_descriptions.ga4_only_dimension',
    'GA4__Ciudad': 'field_descriptions.ga4_only_dimension',
    'GA4__Categoría_dispositivo': 'field_descriptions.ga4_only_dimension',
    'GA4__Sistema_operativo': 'field_descriptions.ga4_only_dimension',
    'GA4__Navegador': 'field_descriptions.ga4_only_dimension',
    'GA4__Página_destino': 'field_descriptions.ga4_only_dimension',
    'GA4__Ruta_página': 'field_descriptions.ga4_only_dimension',
    'GA4__Título_página': 'field_descriptions.ga4_only_dimension',
    'GA4__Nombre_evento': 'field_descriptions.ga4_only_dimension',
    'GA4__ID_transacción': 'field_descriptions.ga4_only_dimension',
    'GA4__ID_item': 'field_descriptions.ga4_only_dimension',
    'GA4__Nombre_item': 'field_descriptions.ga4_only_dimension',
    'GA4__Categoría_item': 'field_descriptions.ga4_only_dimension',
    'GA4__Marca_item': 'field_descriptions.ga4_only_dimension',
    'GA4__Plataforma_fuente': 'field_descriptions.ga4_only_dimension',

    // Agregar más campos según sea necesario
};

/**
 * Obtiene el código de traducción para un campo dado su ID
 * @param {string} fieldId - ID del campo
 * @returns {string|null} - Código de traducción o null si no existe explicación
 */
export function getFieldDescriptionKey(fieldId) {
    return FIELD_DESCRIPTIONS[fieldId] || null;
}

/**
 * Verifica si un campo tiene explicación disponible
 * @param {string} fieldId - ID del campo
 * @returns {boolean} - true si tiene explicación, false en caso contrario
 */
export function hasFieldDescription(fieldId) {
    return fieldId in FIELD_DESCRIPTIONS;
}

